import React from 'react'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { useParams } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { Grid, Divider } from '@mui/material'
import { FormInputRow, ActionButton } from '@emerald-works-nova/components'
import Header from '../header'
import UploadInput from './upload-input'
import PermissionsList from './permissions-list'
import { useForm } from 'react-hook-form'
import { TenantEditSlice, PermissionsUploadSlice, MSATenantConfigSlice } from '../../../../reducers'

const requiredKeys = ['s3Key']

const UserPermissionsUpload = ({ onClickCancel, onUploadStart }) => {
  const dispatch = useDispatch()
  const { id: tenantId } = useParams()
  const { userPoolId } = useSelector(TenantEditSlice.selectors.tenant)
  const msaActive = useSelector(TenantEditSlice.selectors.msaActive)

  const form = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      s3Key: '',
      features: { 'msa:access': msaActive || false }
    }
  })

  const { handleSubmit, setValue, reset, watch } = form

  const [permissionsUpload] = useEvent([PermissionsUploadSlice.eventBus.permissionsUpload])

  const onSubmit = data => {
    permissionsUpload.trigger({ ...data, userPoolId, tenantId })
    onUploadStart()
  }

  const handleClickCancel = () => {
    reset()
    onClickCancel()
  }

  const handleFileAccept = ({ s3Key }) => {
    setValue('s3Key', s3Key)
  }

  useEventsOnViewLoad(() => {
    permissionsUpload.registerAdhocOnSuccessListener(({ payload }) => {
      const { results } = payload
      dispatch(MSATenantConfigSlice.actions.pushUsersToList(results.successList))
    })
  }, [permissionsUpload])

  const values = watch()
  const isLoading = permissionsUpload.isWorking
  const isValid = requiredKeys.every(key => !!values[key])

  return (
    <Grid container>
      <Header
        title='Upload Permissions'
        actions={
          <>
            <Grid item>
              <ActionButton
                variant='secondary'
                dataTest='permissions-upload-cancel-button'
                onClick={handleClickCancel}
              >
                Cancel
              </ActionButton>
            </Grid>
            <Grid item>
              <ActionButton
                variant='primary'
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid}
                isWorking={isLoading}
                dataTest='permissions-upload-upload-button'
              >
                Upload Permissions
              </ActionButton>
            </Grid>
          </>
        }
      />
      <Divider width='100%' />
      <Grid item xs={12} mb={2}>
        <FormInputRow
          title='Products & Feature Access Permissions'
          description='Check the features each user will have permission to access in their toolkit.'
          smallFormTitles
          InputComponent={<PermissionsList setValue={setValue} features={values.features} />}
        />
      </Grid>
      <Divider width='100%' />
      <Grid item xs={12}>
        <FormInputRow
          title='File Upload'
          description='Upload a CSV UTF-8 (Comma delimited) file that contains the Emails of users you want to update permissions for.'
          smallFormTitles
          InputComponent={<UploadInput onAccept={handleFileAccept} />}
        />
      </Grid>
    </Grid>
  )
}

export default UserPermissionsUpload
